/* eslint-disable no-console */
import React, { FC } from 'react';
import DayPicker from 'components/day-picker/DayPicker';
import CustomTimePicker from 'components/time-picker/TimePicker';
import DateUtils from 'utils/dateUtils';
import { isToday } from 'date-fns';
import { SchduleInputErrors } from 'constants/errors';

interface DateTimeSchedulerProps {
  selectedDate: Date;
  setSelectedDate: (date: Date) => void;
  startTime: Date;
  setStartTime: (date: Date) => void;
  endTime: Date;
  setEndTime: (date: Date) => void;
  className?: string;
  startTimeError: string;
  setStartTimeError: (value: string) => void;
  endTimeError: string;
  setEndTimeError: (value: string) => void;
  startTimePickerId: string;
  endTimePickerId: string;
  showPastDates: boolean;
  timePickerCalendarClassName?: string;
}

const DateTimeScheduler: FC<DateTimeSchedulerProps> = ({
  selectedDate,
  setSelectedDate,
  startTime,
  setStartTime,
  setEndTime,
  startTimeError,
  setStartTimeError,
  endTimeError,
  setEndTimeError,
  endTime,
  startTimePickerId,
  endTimePickerId,
  showPastDates,
  timePickerCalendarClassName
}) => {
  const resetErrors = () => {
    setStartTimeError('');
    setEndTimeError('');
  };

  const saveSelectedDate = (value: Date) => {
    setSelectedDate(value);

    const updatedStartTime = DateUtils.updateDatePreservingTime({ date: startTime, newDate: value });
    handleSelectStartTime(updatedStartTime);
    const updatedEndTime = DateUtils.updateDatePreservingTime({ date: endTime, newDate: value });
    handleSelectEndTime(updatedEndTime);

    resetErrors();
  };

  const handleSelectStartTime = (value: Date) => {
    resetErrors();
    setStartTime(value);
    if (new Date(value).getTime() > new Date(endTime).getTime()) {
      setStartTimeError(SchduleInputErrors.Start_Time_Less_Than_End_Time);
    }
    if (!showPastDates && isToday(selectedDate) && new Date(value).getTime() < new Date().getTime()) {
      setStartTimeError(SchduleInputErrors.Start_Time_Less_Than_Current_Time);
    }
  };

  const handleSelectEndTime = (value: Date) => {
    if (startTimeError === SchduleInputErrors.Start_Time_Less_Than_Current_Time) {
      setEndTimeError('');
    } else resetErrors();
    setEndTime(value);
    if (new Date(value).getTime() <= new Date(startTime).getTime()) {
      setEndTimeError(SchduleInputErrors.End_Time_Greater_Than_Start_Time);
    }
  };

  return (
    <div className="flex items-center gap-5">
      <div className="relative z-10 w-1/2">
        <div className="pb-2 text-17px font-semibold text-heading">Date</div>
        <div className="w-full">
          <DayPicker onSelect={saveSelectedDate} date={selectedDate} showPastDates={showPastDates} />
        </div>
      </div>

      <div className="w-1/4">
        <div className="pb-2 text-17px font-semibold text-heading">Start time</div>
        <div className="w-full">
          <CustomTimePicker
            id={startTimePickerId}
            startDate={startTime}
            onSelect={handleSelectStartTime}
            error={startTimeError}
            filterTime={!showPastDates}
            calendarClassName={timePickerCalendarClassName}
          />
        </div>
      </div>
      <div className="w-1/4">
        <div className="pb-2 text-17px font-semibold text-heading">End time</div>
        <div className="w-full">
          <CustomTimePicker
            id={endTimePickerId}
            startDate={endTime}
            onSelect={handleSelectEndTime}
            shouldSetMinTime={true}
            minTime={startTime}
            error={endTimeError}
            filterTime={!showPastDates}
            calendarClassName={timePickerCalendarClassName}
          />
        </div>
      </div>
    </div>
  );
};

export default DateTimeScheduler;
